import type { TrainingAnswers, TrainingPlayerQuestionGroup } from '@hcc/types';

/**
 * A question group: several questions sharing one set of answer columns,
 * laid out as a grid (the assessment classic, "Is your keyboard separate
 * from the screen? No / Yes"), with an optional illustration placed around
 * it. Each row is an independent question, so answers are keyed by row id.
 *
 * A table is the honest markup here: the column headers label every cell in
 * the column, which is exactly what a screen reader needs.
 */

const IMAGE_SIZE_CLASS = {
  small: 'max-h-40',
  medium: 'max-h-80',
  large: 'max-h-[32rem]',
} as const;

const IMAGE_ALIGN_CLASS = {
  left: 'items-start',
  center: 'items-center',
  right: 'items-end',
} as const;

export function QuestionGroupSlide({
  group,
  answers,
  onChange,
  disabled,
}: {
  group: TrainingPlayerQuestionGroup;
  answers: TrainingAnswers;
  onChange: (rowId: string, optionId: string) => void;
  disabled?: boolean;
}) {
  const position = group.image_key ? (group.image_position ?? 'below') : undefined;
  const beside = position === 'left' || position === 'right';
  const grid = (
    <GroupGrid group={group} answers={answers} onChange={onChange} disabled={disabled} />
  );
  const image = group.image_key ? <GroupImage group={group} /> : null;

  return (
    <div className="space-y-4">
      {group.title ? <h2 className="text-lg font-semibold text-fg">{group.title}</h2> : null}
      {group.intro ? <p className="text-sm text-fg-muted">{group.intro}</p> : null}
      {beside ? (
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
          {position === 'left' ? <div className="sm:w-2/5">{image}</div> : null}
          <div className="min-w-0 flex-1">{grid}</div>
          {position === 'right' ? <div className="sm:w-2/5">{image}</div> : null}
        </div>
      ) : (
        <>
          {position === 'above' ? image : null}
          {grid}
          {position === 'below' ? image : null}
        </>
      )}
    </div>
  );
}

function GroupGrid({
  group,
  answers,
  onChange,
  disabled,
}: {
  group: TrainingPlayerQuestionGroup;
  answers: TrainingAnswers;
  onChange: (rowId: string, optionId: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="w-full px-1 pb-2 text-left" />
            {group.options.map((o) => (
              <th
                key={o.id}
                scope="col"
                className="whitespace-nowrap px-3 pb-2 text-center text-xs font-semibold uppercase tracking-wide text-fg-muted"
              >
                {o.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-divider/10">
          {group.rows.map((row) => {
            const value = typeof answers[row.id] === 'string' ? (answers[row.id] as string) : '';
            return (
              <tr key={row.id} className="border-t border-line/10">
                <th scope="row" className="py-3 pr-3 text-left font-normal text-fg">
                  {row.prompt}
                  {row.optional ? (
                    <span className="ml-1.5 text-xs text-fg-dim">(optional)</span>
                  ) : null}
                </th>
                {group.options.map((o) => (
                  <td key={o.id} className="px-3 py-3 text-center align-middle">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={value === o.id}
                      aria-label={`${row.prompt}: ${o.label}`}
                      disabled={disabled}
                      onClick={() => onChange(row.id, o.id)}
                      className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-token-sm transition-colors hover:bg-surface-2 disabled:opacity-60"
                    >
                      <span
                        aria-hidden
                        className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                          value === o.id ? 'border-accent' : 'border-line-strong/40'
                        }`}
                      >
                        {value === o.id ? (
                          <span className="h-2.5 w-2.5 rounded-full bg-accent" />
                        ) : null}
                      </span>
                    </button>
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GroupImage({ group }: { group: TrainingPlayerQuestionGroup }) {
  const align = IMAGE_ALIGN_CLASS[group.image_align ?? 'center'];
  return (
    <figure className={`flex flex-col ${align}`}>
      <img
        src={group.image_key}
        alt={group.image_alt ?? ''}
        className={`${IMAGE_SIZE_CLASS[group.image_size ?? 'medium']} max-w-full rounded-token-sm border border-line/10`}
      />
      {group.image_caption ? (
        <figcaption className="mt-1.5 text-xs text-fg-dim">{group.image_caption}</figcaption>
      ) : null}
    </figure>
  );
}
